<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col sm="12" md="6">
        <b-card>
          <div
            slot="header"
            class="d-flex justify-content-between align-items-center"
          >
            <span>
              <font-awesome-icon icon="globe" style="margin-right: 5px" />
              Server Listing
            </span>
            <b-badge
              :variant="statusBadge.variant"
              style="font-size: 0.85rem; padding: 0.4rem 0.7rem"
            >
              {{ statusBadge.text }}
            </b-badge>
          </div>

          <!-- Loading State -->
          <div v-if="loading">
            <div class="sk-circle-fade float-left">
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
            </div>
            <h2 class="float-left ml-4">Loading...</h2>
          </div>

          <!-- Form -->
          <div v-else>
            <!-- Missing Bot Permissions Alert -->
            <div v-if="!botPermsOk" class="perms-alert mb-4">
              <div class="perms-alert-icon">
                <font-awesome-icon icon="exclamation-triangle" />
              </div>
              <div>
                <strong>Missing Permissions</strong>
                <p class="mb-0 mt-1">
                  Carl-bot is missing the
                  <code>Create Invite</code> permission. Please grant it before
                  submitting your listing. Please also note that once the
                  permission is granted it might take a few minutes to reflect
                  here.
                </p>
              </div>
            </div>

            <!-- Rejection / Suspension Reason Alert -->
            <b-alert
              v-if="(status === 3 || status === 4) && reason"
              variant="danger"
              show
              class="mb-4"
            >
              <strong>{{ status === 3 ? 'Your listing was rejected' : 'Your listing was suspended' }}</strong>
              <p class="mb-0 mt-1">{{ reason }}</p>
            </b-alert>

            <fieldset :disabled="!botPermsOk">
              <!-- Description -->
              <b-form-group>
                <h4 class="tinytitle">Description</h4>
                <b-form-textarea
                  v-model="form.description"
                  placeholder="Describe your server..."
                  rows="4"
                  max-rows="6"
                  :maxlength="500"
                ></b-form-textarea>
                <small>{{ form.description.length }}/500</small>
              </b-form-group>

              <hr />

              <!-- Category -->
              <b-form-group>
                <h4 class="tinytitle">Category</h4>
                <b-form-select
                  v-model="form.category"
                  :options="categoryOptions"
                ></b-form-select>
              </b-form-group>

              <hr />

              <!-- Tags -->
              <b-form-group>
                <h4 class="tinytitle">Tags (Max 5)</h4>
                <b-form-input
                  v-model="tagInput"
                  placeholder="Add tags (press enter to apply)"
                  @keydown.enter.prevent="addTag"
                ></b-form-input>
                <div v-if="form.tags.length > 0" class="mt-2">
                  <b-badge
                    v-for="(tag, index) in form.tags"
                    :key="index"
                    variant="secondary"
                    class="mr-1 mb-1"
                    style="font-size: 0.9rem; padding: 0.4rem 0.6rem"
                  >
                    {{ tag }}
                    <font-awesome-icon
                      icon="times"
                      style="cursor: pointer; margin-left: 5px"
                      @click="removeTag(index)"
                    />
                  </b-badge>
                </div>
                <small
                  v-if="form.tags.length >= 5"
                  class="text-warning d-block mt-1"
                >
                  Maximum 5 tags allowed
                </small>
              </b-form-group>

              <hr />

              <!-- NSFW Toggle -->
              <b-form-group>
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 class="tinytitle mb-0">NSFW Content</h4>
                    <small class="text-muted"
                      >Mark if your server contains NSFW content</small
                    >
                  </div>
                  <b-form-checkbox
                    v-model="form.nsfw"
                    switch
                    size="lg"
                  ></b-form-checkbox>
                </div>
              </b-form-group>

              <hr />

              <!-- Channel Select -->
              <b-form-group>
                <h4 class="tinytitle">Listing Channel</h4>
                <small class="text-muted d-block mb-2"
                  >Select the channel users will be directed to when
                  joining</small
                >
                <multiselect
                  v-model="form.channel"
                  track-by="id"
                  label="name"
                  placeholder="Select a channel"
                  open-direction="bottom"
                  :searchable="true"
                  :clear-on-select="false"
                  :close-on-select="true"
                  :max-height="600"
                  :show-no-results="false"
                  :hide-selected="false"
                  :options="groupedChannels"
                  :allow-empty="false"
                  group-values="channels"
                  group-label="category"
                  :group-select="false"
                >
                  <template slot="singleLabel" slot-scope="props">
                    <span class="option__desc">
                      <font-awesome-icon
                        style="color: #72767d"
                        icon="hashtag"
                      />
                      <span class="option__title ml-2">{{
                        props.option.name
                      }}</span>
                    </span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <div class="option__desc">
                      <span class="option__title">
                        <font-awesome-icon
                          style="color: #72767d"
                          :icon="
                            !props.option['$isLabel']
                              ? 'hashtag'
                              : 'chevron-down'
                          "
                        />
                        {{ props.option.name || props.option.$groupLabel.name }}
                      </span>
                    </div>
                  </template>
                </multiselect>
              </b-form-group>

              <hr />

              <!-- Role Select -->
              <b-form-group>
                <h4 class="tinytitle">Server Bump Role</h4>
                <small class="text-muted d-block mb-2"
                  >Select an optional role to assign to users who bump the server</small
                >
                <multiselect
                  v-model="form.role"
                  track-by="id"
                  label="name"
                  placeholder="Select a role"
                  open-direction="bottom"
                  :searchable="true"
                  :clear-on-select="false"
                  :close-on-select="true"
                  :max-height="600"
                  :show-no-results="false"
                  :hide-selected="false"
                  :options="roles"
                  :allow-empty="true"
                  deselect-label="Click to deselect"
                >
                  <template slot="singleLabel" slot-scope="props">
                    <span class="option__desc">
                      <span class="option__title" :style="{'color': parseColor(props.option)}">{{
                        props.option.name
                      }}</span>
                    </span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <div class="option__desc" :style="{'color': parseColor(props.option)}">
                      <span class="option__title">
                        {{ props.option.name }}
                      </span>
                    </div>
                  </template>
                </multiselect>
                <p class="text-muted mt-2 mb-0" style="font-size: 0.9rem;">
                  <b>Note:</b> This only shows safe roles which do not have any manage permissions.
                </p>
              </b-form-group>

              <hr />
            </fieldset>

            <!-- Action Buttons -->
            <div class="d-flex" style="gap: 10px">
              <!-- Save Button -->
              <b-button
                variant="primary"
                @click="saveListing"
                :disabled="saving || delisting || !botPermsOk"
              >
                <font-awesome-icon
                  v-if="saving"
                  icon="spinner"
                  spin
                  style="margin-right: 5px"
                />
                Submit
              </b-button>

              <!-- Cancel Review Button (status 1 = In Review) -->
              <b-button
                v-if="status === 1"
                variant="warning"
                @click="showConfirmModal = true"
                :disabled="saving || delisting"
              >
                Cancel Review
              </b-button>

              <!-- Unpublish Button (status 2 = Approved) -->
              <b-button
                v-if="status === 2"
                variant="danger"
                @click="showUnpublishConfirmModal = true"
                :disabled="saving || delisting"
              >
                Unpublish Server
              </b-button>
            </div>
          </div>
        </b-card>
      </b-col>

      <!-- Server Modifiers Card -->
      <b-col sm="12" md="6">
        <b-card>
          <div
            slot="header"
            class="d-flex justify-content-between align-items-center"
          >
            <span>
              Server Perks
            </span>
          </div>

          <!-- Loading State -->
          <div v-if="loadingModifiers">
            <div class="sk-circle-fade float-left">
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
              <div class="sk-grid-cube"></div>
            </div>
            <h2 class="float-left ml-4">Loading...</h2>
          </div>

          <!-- Content -->
          <div v-else>
            <div v-if="isListed && rankData && rankData.rank != null">
              <b-form-group>

                <div style="font-size: 1.1rem; color: #fff;">
                  Your server is ranked <strong style="color: #43b581;">{{ rankData.rank }}</strong> out of <strong style="color: #f04747;">{{ rankData.total_servers }}</strong> total servers
                </div>
              </b-form-group>
              <hr />
            </div>

            <!-- Active Perks -->
            <div v-if="activeBoosts && activeBoosts.length > 0">
              <b-form-group>
                <h4 class="tinytitle mb-0">Active Perks</h4>
                <div class="table-responsive mt-2">
                  <table class="superbump-table">
                    <thead>
                      <tr>
                        <th class="col-perk">Perk</th>
                        <th class="col-user">User</th>
                        <th class="col-expires">Expires</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="boost in paginatedActiveBoosts" :key="boost.entitlement_id">
                        <td class="col-perk">{{ boost.perk_name || "—" }}</td>
                        <td class="col-user">{{ boost.username || boost.user_id }}</td>
                        <td class="col-expires">{{ formatExpiry(boost.timestamp) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="mt-2" style="font-size: 0.85rem; color: #fff;">
                  Total Superbumps: <strong style="color: #fff176;">+{{ totalActiveBumps }}</strong><br />
                  Total Multiplier: <strong style="color: #fff176;">x{{ totalActiveMultipliers }}</strong>
                </div>
                <div v-if="activeBoosts.length > activePerksPerPage" class="d-flex mt-2">
                  <div class="col-perk-user-spacer"></div>
                  <b-pagination
                    v-model="activePerksPage"
                    :total-rows="activeBoosts.length"
                    :per-page="activePerksPerPage"
                    align="left"
                    size="sm"
                    class="mb-0 flex-grow-1"
                  ></b-pagination>
                </div>
              </b-form-group>
              <hr />
            </div>

            <!-- Manage Perks -->
            <div>
              <b-form-group>
                <h4 class="tinytitle mb-0">Manage Perks</h4>
                <div v-if="boosts && boosts.length > 0" class="boost-list mt-3">
                  <div
                    v-for="group in boosts"
                    :key="boostKey(group.perk_type, group.perk_value)"
                    class="d-flex align-items-center py-2"
                  >
                    <div class="col-perk-user-spacer">
                      <span style="font-size: 1rem; color: #fff;">
                        {{ group.perk_name }} ({{ group.count }} remaining)
                      </span>
                    </div>
                    <div class="flex-grow-1">
                      <b-button
                        size="sm"
                        variant="primary"
                        :disabled="activatingBoosts[boostKey(group.perk_type, group.perk_value)] || !isListed"
                        @click="promptActivateBoost(group.perk_type, group.perk_name, group.perk_value)"
                      >
                        <font-awesome-icon
                          v-if="activatingBoosts[boostKey(group.perk_type, group.perk_value)]"
                          icon="spinner"
                          spin
                          style="margin-right: 5px;"
                        />
                        Activate
                      </b-button>
                    </div>
                  </div>
                </div>
                <p v-else class="text-muted mt-2 mb-0" style="font-size: 0.85rem;">You do not have any available perks</p>
                <p class="text-muted mt-2 mb-0" style="font-size: 0.85rem;">
                  <b>Note:</b> Activating a perk is only available once your server is publicly listed.
                </p>
              </b-form-group>
              <hr />
            </div>

            <p class="text-muted mb-3" style="font-size: 0.9rem;">
              Server Perks are only available for purchase through Discord store.
            </p>

            <div class="d-flex" style="gap: 10px">
              <b-button
                class="discord-store-btn"
                :href="storeLink"
                :disabled="!storeLink"
                target="_blank"
              >
                <i class="fas fa-store mr-2"></i>Store
              </b-button>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Confirmation Modal for Cancel Review -->
    <b-modal
      v-model="showConfirmModal"
      centered
      hide-footer
      :no-enforce-focus="true"
    >
      <template slot="modal-title">
        <h4 class="smalltitle">
          <font-awesome-icon
            icon="exclamation-triangle"
            style="color: #faa61a; margin-right: 8px"
          />
          Cancel Review?
        </h4>
      </template>

      <div class="text-center py-3">
        <p>
          This will remove your server from the review queue and reset your
          listing form. You can always resubmit later.
        </p>
      </div>

      <div class="d-flex justify-content-center pb-2" style="gap: 10px">
        <b-button
          variant="warning"
          @click="confirmDelist"
          :disabled="delisting"
        >
          <font-awesome-icon
            v-if="delisting"
            icon="spinner"
            spin
            style="margin-right: 5px"
          />
          Yes, Cancel Review
        </b-button>
        <b-button
          variant="secondary"
          @click="showConfirmModal = false"
          :disabled="delisting"
        >
          Go Back
        </b-button>
      </div>
    </b-modal>

    <!-- Confirmation Modal for Unpublishing the Server -->
    <b-modal
      v-model="showUnpublishConfirmModal"
      centered
      hide-footer
      :no-enforce-focus="true"
    >
      <template slot="modal-title">
        <h4 class="smalltitle">
          <font-awesome-icon
            icon="exclamation-triangle"
            style="color: #faa61a; margin-right: 8px"
          />
          Unpublish Server?
        </h4>
      </template>

      <div class="text-center py-3">
        <p>
          This will remove the server from public listings. You can always
          relist later.
        </p>
        <p class="text-warning mb-0" style="font-size: 0.85rem;">
          <font-awesome-icon
            icon="exclamation-triangle"
            style="margin-right: 5px"
          />
          If you have perks enabled on this server, they will get disabled.
          However, their duration will still be consumed.
        </p>
      </div>

      <div class="d-flex justify-content-center pb-2" style="gap: 10px">
        <b-button
          class="unpublish-btn"
          @click="confirmDelist"
          :disabled="delisting"
        >
          <font-awesome-icon
            v-if="delisting"
            icon="spinner"
            spin
            style="margin-right: 5px"
          />
          Yes, Unpublish
        </b-button>
        <b-button
          variant="secondary"
          @click="showUnpublishConfirmModal = false"
          :disabled="delisting"
        >
          Go Back
        </b-button>
      </div>
    </b-modal>

    <!-- Confirmation Modal for Activating a Superbump -->
    <b-modal
      v-model="showBoostConfirmModal"
      centered
      hide-footer
      :no-enforce-focus="true"
    >
      <template slot="modal-title">
        <h4 class="smalltitle">
          <font-awesome-icon
            icon="exclamation-triangle"
            style="color: #faa61a; margin-right: 8px"
          />
          Activate {{ pendingPerkName }}?
        </h4>
      </template>

      <div class="text-center py-3">
        <p>
          This will activate the {{ pendingPerkName }} in this server. This action
          cannot be undone.
        </p>
      </div>

      <div class="d-flex justify-content-center pb-2" style="gap: 10px">
        <b-button
          variant="primary"
          @click="confirmActivateBoost"
          :disabled="activatingBoosts[boostKey(pendingPerkType, pendingPerkValue)]"
        >
          <font-awesome-icon
            v-if="activatingBoosts[boostKey(pendingPerkType, pendingPerkValue)]"
            icon="spinner"
            spin
            style="margin-right: 5px"
          />
          Yes, Activate
        </b-button>
        <b-button
          variant="secondary"
          @click="showBoostConfirmModal = false"
          :disabled="activatingBoosts[boostKey(pendingPerkType, pendingPerkValue)]"
        >
          Go Back
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import miniToastr from "mini-toastr";
import Multiselect from "vue-multiselect";

const toastTypes = {
  success: "success",
  error: "error",
  info: "info",
  warn: "warn",
};
miniToastr.init({ types: toastTypes });

function toast({ title, message, type, timeout, cb }) {
  return miniToastr[type](message, title, timeout, cb);
}

const options = {
  success: toast,
  error: toast,
  info: toast,
  warn: toast,
};

Vue.use(VueNotifications, options);

export default {
  name: "ServerListing",
  components: {
    Multiselect,
  },
  data() {
    return {
      loading: true,
      loadingModifiers: true,
      rankData: null,
      boosts: [],
      activeBoosts: [],
      totalActiveBumps: 0,
      totalActiveMultipliers: 0,
      activePerksPage: 1,
      activePerksPerPage: 5,
      storeLink: null,
      activatingBoosts: {},
      saving: false,
      delisting: false,
      status: null,
      reason: null,
      botPermsOk: true,
      showConfirmModal: false,
      showUnpublishConfirmModal: false,
      showBoostConfirmModal: false,
      pendingPerkType: null,
      pendingPerkName: null,
      pendingPerkValue: null,
      channels: [],
      roles: [],
      form: {
        description: "",
        category: "",
        tags: [],
        nsfw: false,
        channel: null,
        role: null,
      },
      originalForm: null,
      tagInput: "",
      categoryOptions: [
        { value: "", text: "Select a category...", disabled: true },
      ],
    };
  },
  notifications: {
    showSuccessMsg: {
      type: VueNotifications.types.success,
      title: "Success!",
      message: "Server listing saved successfully!",
    },
    showNoChanges: {
      type: VueNotifications.types.info,
      title: "Uh-oh",
      message: "Make changes to your listing before saving.",
    },
    showErrorMaxTags: {
      type: VueNotifications.types.error,
      title: "Uh-oh",
      message: "Maximum 5 tags allowed",
    },
    showErrorDuplicateTag: {
      type: VueNotifications.types.error,
      title: "Uh-oh",
      message: "Tag already added",
    },
    showErrorTagTooLong: {
      type: VueNotifications.types.error,
      title: "Uh-oh",
      message: "Tag too long (max 10 characters)",
    },
    showErrorNoCategory: {
      type: VueNotifications.types.error,
      title: "Uh-oh",
      message: "Please select a category",
    },
    showErrorDescriptionShort: {
      type: VueNotifications.types.error,
      title: "Uh-oh",
      message: "Description must be at least 20 characters",
    },
    showErrorDescriptionLong: {
      type: VueNotifications.types.error,
      title: "Uh-oh",
      message: "Description must not exceed 500 characters",
    },
    showErrorNoChannel: {
      type: VueNotifications.types.error,
      title: "Uh-oh",
      message: "Please select a channel",
    },
    showErrorMissingPerms: {
      type: VueNotifications.types.error,
      title: "Missing Permissions",
      message:
        "Carl-bot is missing the Create Invite permission in the selected channel.",
    },
    showErrorSaveFailed: {
      type: VueNotifications.types.error,
      title: "Error",
      message: "Failed to save listing. Please try again.",
    },
    showDelistSuccess: {
      type: VueNotifications.types.success,
      title: "Done",
      message: "Your server has been removed from the public listings.",
    },
    showCancelReviewSuccess: {
      type: VueNotifications.types.success,
      title: "Done",
      message: "Your server has been removed from the review queue.",
    },
    showDelistFailed: {
      type: VueNotifications.types.error,
      title: "Error",
      message: "Failed to process request. Please try again.",
    },
    showErrorFetchRankFailed: {
      type: VueNotifications.types.error,
      title: "Error",
      message: "Failed to load rank details. Please try again.",
    },
    showErrorFetchBoostsFailed: {
      type: VueNotifications.types.error,
      title: "Error",
      message: "Failed to load available boosts.",
    },
    showSuccessActivateBoost: {
      type: VueNotifications.types.success,
      title: "Activated!",
      message: "Successfully activated perk!",
    },
    showErrorActivateBoostFailed: {
      type: VueNotifications.types.error,
      title: "Activation Failed",
      message: "Failed to activate boost. Please try again.",
    },
  },
  created() {
    // loadModifiers relies on `status` (from loadListing) to decide whether
    // to fetch the rank, so it must run after loadListing resolves. The
    // other calls are independent and can still fire immediately.
    const listingPromise = this.loadListing();
    this.checkBotPerms();
    this.fetchCategories();
    listingPromise.then(() => this.loadModifiers());
  },
  methods: {
    snapshotForm() {
      this.originalForm = {
        description: this.form.description,
        category: this.form.category,
        tags: [...this.form.tags],
        nsfw: this.form.nsfw,
        channel: this.form.channel ? this.form.channel.id : null,
        role: this.form.role ? this.form.role.id : null,
      };
    },

    resetForm() {
      this.form.description = "";
      this.form.category = "";
      this.form.tags = [];
      this.form.nsfw = false;
      this.form.channel = { name: "No channel selected", id: null };
      this.form.role = null;
      this.tagInput = "";
      this.originalForm = null;
    },

    async loadModifiers() {
      this.loadingModifiers = true;
      try {
        const tasks = [this.fetchBoosts()];
        if (this.isListed) {
          tasks.push(this.fetchRank());
        } else {
          // Server isn't publicly listed, so it has no rank to show.
          this.rankData = null;
        }
        await Promise.all(tasks);
      } catch (error) {
        console.error("Error loading modifiers:", error);
      } finally {
        this.loadingModifiers = false;
      }
    },

    async fetchRank() {
      try {
        const res = await this.axios.get(
          `/api/v1/servers/${this.$route.params.guild_id}/rank`
        );
        if (res.data && res.data.success) {
          this.rankData = res.data.rank_data || null;
        } else {
          const msg = res.data ? res.data.message : null;
          this.showErrorFetchRankFailed(msg ? { message: msg } : undefined);
        }
      } catch (error) {
        console.error("Error fetching rank:", error);
        let msg = null;
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message;
        }
        if (error.response && error.response.status === 429) {
          this.showErrorFetchRankFailed({
            message: msg || "You are being rate limited. Please try again later.",
          });
        } else {
          this.showErrorFetchRankFailed(msg ? { message: msg } : undefined);
        }
      }
    },

    async fetchBoosts() {
      try {
        const res = await this.axios.get(
          `/api/v1/servers/${this.$route.params.guild_id}/boosts`
        );
        if (res.data && res.data.success) {
          this.boosts = res.data.boosts || [];
          this.activeBoosts = res.data.active_boosts || [];
          this.totalActiveBumps = res.data.total_active_bumps || 0;
          this.totalActiveMultipliers = res.data.total_active_multipliers || 0;
          this.activePerksPage = 1;
          this.storeLink = res.data.store_link || null;
        } else {
          const msg = res.data ? res.data.message : null;
          this.showErrorFetchBoostsFailed(msg ? { message: msg } : undefined);
        }
      } catch (error) {
        console.error("Error fetching boosts:", error);
        let msg = null;
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message;
        }
        if (error.response && error.response.status === 429) {
          this.showErrorFetchBoostsFailed({
            message: msg || "You are being rate limited. Please try again later.",
          });
        } else {
          this.showErrorFetchBoostsFailed(msg ? { message: msg } : undefined);
        }
        this.activeBoosts = [];
        this.totalActiveBumps = 0;
        this.totalActiveMultipliers = 0;
      }
    },

    boostKey(perkType, perkValue) {
      return `${perkType}_${perkValue}`;
    },

    promptActivateBoost(perkType, perkName, perkValue) {
      this.pendingPerkType = perkType;
      this.pendingPerkName = perkName;
      this.pendingPerkValue = perkValue;
      this.showBoostConfirmModal = true;
    },

    async confirmActivateBoost() {
      const perkType = this.pendingPerkType;
      const perkValue = this.pendingPerkValue;
      this.showBoostConfirmModal = false;
      if (perkType == null) return;
      await this.activateBoost(perkType, perkValue);
      this.pendingPerkType = null;
      this.pendingPerkName = null;
      this.pendingPerkValue = null;
    },

    async activateBoost(perkType, perkValue) {
      const key = this.boostKey(perkType, perkValue);
      Vue.set(this.activatingBoosts, key, true);

      try {
        const res = await this.axios.post(
          `/api/v1/servers/${this.$route.params.guild_id}/boosts`,
          { perk_type: perkType, perk_value: perkValue }
        );
        if (res.data && res.data.success) {
          const perkName = this.pendingPerkName || "perk";
          this.showSuccessActivateBoost({
            message: `Successfully activated ${perkName}!`,
          });
          await this.fetchBoosts();
        } else {
          const msg = res.data ? res.data.message : null;
          this.showErrorActivateBoostFailed(msg ? { message: msg } : undefined);
        }
      } catch (error) {
        console.error("Error activating boost:", error);
        let msg = null;
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message;
        }
        if (error.response && error.response.status === 429) {
          this.showErrorActivateBoostFailed({
            message: msg || "You are being rate limited. Please try again later.",
          });
        } else {
          this.showErrorActivateBoostFailed(msg ? { message: msg } : undefined);
        }
      } finally {
        Vue.set(this.activatingBoosts, key, false);
      }
    },

    async loadListing() {
      this.loading = true;
      try {
        const res = await this.axios.get(
          `/api/v1/servers/${this.$route.params.guild_id}/listing`,
        );

        // Pre-fill form if listing exists
        if (res.data.listed) {
          this.form.description = res.data.description || "";
          this.form.category = res.data.category || "";
          this.form.tags = res.data.tags || [];
          this.form.nsfw = res.data.nsfw || false;

          this.status = res.data.status;
          this.reason = res.data.reason || null;
        }
        // Always populate channels from response
        this.channels = res.data.channels || [];
        this.form.channel = this.channels.find(
          (x) => x.id == res.data.channel_id,
        ) || { name: "No channel selected", id: null };
        console.log(this.form.channel);

        // Populate roles
        this.roles = res.data.roles || [];
        if (res.data.role_id) {
          this.form.role = this.roles.find((x) => x.id == res.data.role_id) || null;
        } else {
          this.form.role = null;
        }
        if (this.form.channel.type === 4) {
          this.form.channel = { name: "No channel selected", id: null };
        }

        // Snapshot the loaded form state
        this.snapshotForm();
      } catch (error) {
        console.error("Error loading listing:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchCategories() {
      try {
        const res = await this.axios.get("/api/v1/listing-categories");
        if (res.data.success && res.data.data) {
          const fetched = res.data.data.map((c) => ({ value: c, text: c }));
          this.categoryOptions = [
            { value: "", text: "Select a category...", disabled: true },
            ...fetched,
          ];
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    },

    addTag() {
      const tag = this.tagInput.trim().toLowerCase();

      // Validate tag
      if (!tag) {
        return;
      }

      if (this.form.tags.length >= 5) {
        this.showErrorMaxTags();
        return;
      }

      if (this.form.tags.includes(tag)) {
        this.showErrorDuplicateTag();
        this.tagInput = "";
        return;
      }

      if (tag.length > 10) {
        this.showErrorTagTooLong();
        return;
      }

      // Add tag
      this.form.tags.push(tag);
      this.tagInput = "";
    },

    removeTag(index) {
      this.form.tags.splice(index, 1);
    },

    async checkBotPerms() {
      try {
        const res = await this.axios.post(
          `/api/v1/servers/${this.$route.params.guild_id}/bot-permissions`,
          {
            required_permissions: ["CREATE_INSTANT_INVITE"],
          },
        );
        this.botPermsOk = res.data.has_all_permissions;
      } catch (error) {
        console.error("Error checking bot permissions:", error);
      }
    },

    async saveListing() {
      // Bail out if nothing changed on an existing listing
      if (!this.hasChanges) {
        this.showNoChanges();
        return;
      }

      // Validate form
      if (!this.form.category) {
        this.showErrorNoCategory();
        return;
      }

      if (!this.form.channel || !this.form.channel.id) {
        this.showErrorNoChannel();
        return;
      }

      if (this.form.description.trim().length < 20) {
        this.showErrorDescriptionShort();
        return;
      }

      if (this.form.description.trim().length > 500) {
        this.showErrorDescriptionLong();
        return;
      }

      this.saving = true;

      try {
        const payload = {
          category: this.form.category,
          description: this.form.description.trim(),
          tags: this.form.tags,
          nsfw: this.form.nsfw,
          channel_id: this.form.channel.id,
          role_id: this.form.role ? this.form.role.id : null,
        };

        const res = await this.axios.post(
          `/api/v1/servers/${this.$route.params.guild_id}/listing`,
          payload,
        );

        if (res.data.success) {
          this.showSuccessMsg();
        } else {
          const msg = res.data.message;
          this.showErrorSaveFailed(msg ? { message: msg } : undefined);
        }
      } catch (error) {
        console.error("Error saving listing:", error);
        let msg = null;
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message;
        }

        if (error.response && error.response.status == 429) {
          this.showErrorSaveFailed({
            message: msg || "You are being rate limited. Please try again later.",
          });
        } else {
          this.showErrorSaveFailed(msg ? { message: msg } : undefined);
        }
      } finally {
        this.saving = false;
        await this.loadListing();
      }
    },

    async confirmDelist() {
      var wasInReview = this.status === 1;
      this.delisting = true;
      try {
        const res = await this.axios.delete(
          `/api/v1/servers/${this.$route.params.guild_id}/delist`,
        );
        if (res.data.success) {
          if (wasInReview) {
            this.showCancelReviewSuccess();
          } else {
            this.showDelistSuccess();
          }
          this.status = null;
          this.reason = null;
          this.showConfirmModal = false;
          this.showUnpublishConfirmModal = false;
          this.resetForm();
        }
      } catch (error) {
        console.error("Error delisting server:", error);
        if (error.response && error.response.status === 429) {
          this.showDelistFailed({
            message: "You are being rate limited. Please try again later.",
          });
        } else {
          this.showDelistFailed();
        }
      } finally {
        this.delisting = false;
      }
    },
    parseColor(r) {
      if (!r || r.color == null || r.color === 0) return "#fefefe";
      const hex = r.color.toString(16);
      return "#" + hex.padStart(6, '0');
    },
    formatExpiry(timestamp) {
      const expiry = new Date(timestamp);
      expiry.setDate(expiry.getDate() + 30);
      return expiry.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    },
  },
  computed: {
    isListed() {
      return this.status === 2;
    },
    paginatedActiveBoosts() {
      const start = (this.activePerksPage - 1) * this.activePerksPerPage;
      return this.activeBoosts.slice(start, start + this.activePerksPerPage);
    },
    hasChanges() {
      if (!this.originalForm) return true; // new listing, always allow
      return (
        this.form.description !== this.originalForm.description ||
        this.form.category !== this.originalForm.category ||
        this.form.nsfw !== this.originalForm.nsfw ||
        (this.form.channel ? this.form.channel.id : null) !==
          this.originalForm.channel ||
        (this.form.role ? this.form.role.id : null) !==
          this.originalForm.role ||
        JSON.stringify(this.form.tags) !==
          JSON.stringify(this.originalForm.tags)
      );
    },
    groupedChannels() {
      let groups = [
        {
          category: { id: null, name: "Uncategorized" },
          channels: [],
        },
      ];
      let currentGroup = groups[0];
      for (let chn of this.channels.sort((a, b) => a.position - b.position)) {
        if (chn.type === 4) {
          currentGroup = { category: chn, channels: [] };
          groups.push(currentGroup);
        }
      }
      for (let chn of this.channels.sort((a, b) => a.position - b.position)) {
        if (chn.type === 4) continue;
        currentGroup = groups.find((g) => g.category.id == chn.parent_id);
        if (!currentGroup) continue;
        currentGroup.channels.push(chn);
      }
      return groups;
    },
    statusBadge() {
      switch (this.status) {
        case 1:
          return { variant: "warning", text: "🟡 In Review" };
        case 2:
          return { variant: "success", text: "🟢 Approved" };
        case 3:
          return { variant: "danger", text: "🔴 Rejected" };
        case 4:
          return { variant: "danger", text: "🚫 Suspended" };
        default:
          return { variant: "secondary", text: "⚫ Not Listed" };
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tinytitle {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.perms-alert {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background-color: #1a2035;
  border: 1px solid #3a5199;
  border-left: 4px solid #5b7fe8;
  border-radius: 4px;
  padding: 12px 16px;
  color: #a0b4f0;
}

.perms-alert-icon {
  font-size: 1.2rem;
  padding-top: 2px;
  flex-shrink: 0;
  color: #5b7fe8;
}

.perms-alert strong {
  color: #c5d0f5;
  display: block;
}

.perms-alert p {
  color: #8fa3d8;
  font-size: 0.9rem;
}

.perms-alert code {
  background-color: #243060;
  color: #a0b4f0;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 0.85rem;
}

.superbump-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th {
    text-align: left;
    padding: 4px 10px 6px 0;
    color: #a3a6ab;
    font-weight: 600;
    border-bottom: 1px solid #4f545c;
  }

  td {
    padding: 6px 10px 6px 0;
    color: #b9bbbe;
    border-bottom: 1px solid #3a3d44;
  }

  tr:last-child td {
    border-bottom: none;
  }

  .col-perk {
    width: 45%;
  }

  .col-user {
    width: 30%;
  }

  .col-expires {
    width: 25%;
  }
}

// Mirrors the table's Perk + User column widths (45% + 30%) so the
// pagination bar lines up under the start of the Expires column instead
// of sitting flush against the far right corner.
.col-perk-user-spacer {
  flex: 0 0 75%;
  max-width: 75%;
}

.discord-store-btn {
  background-color: #5865f2 !important;
  border-color: #5865f2 !important;
  color: #fff !important;
  font-weight: 600;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background-color: #4752c4 !important;
    border-color: #4752c4 !important;
  }
}

.unpublish-btn {
  background-color: #d9534f !important;
  border-color: #d9534f !important;
  color: #fff !important;
  font-weight: 600;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background-color: #c9302c !important;
    border-color: #c9302c !important;
  }
}
</style>
